import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePathLync } from "@/pathlync/PathLyncAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Microscope, Loader2, Building2, Stethoscope, ArrowRight, Check } from "lucide-react";

const POINTS = [
  "Upload gross and microscopic images straight from the bench camera",
  "A consulting pathologist reviews, annotates and signs remotely",
  "A signed PDF consultation report comes back to your laboratory",
];

export default function PathLyncAuthPage() {
  const { user, checking, signIn, register } = usePathLync();
  const location = useLocation();
  // After a logged-out deep link, come back to the page that was asked for.
  const from = location.state?.from || "/pathlync/dashboard";
  const [mode, setMode] = useState("signin");
  const [role, setRole] = useState("lab_user");
  const [form, setForm] = useState({ name: "", email: "", password: "", institution: "", credentials: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (checking) {
    return <div className="grid min-h-screen place-items-center bg-slate-50">
      <Loader2 className="animate-spin text-slate-400" size={26} />
    </div>;
  }
  if (user) return <Navigate to={from} replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = mode === "signin"
      ? await signIn({ email: form.email, password: form.password })
      : await register({ ...form, role });
    setBusy(false);
    // On success the `user` in context flips and the <Navigate> below redirects; calling
    // navigate() here as well races that render.
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen bg-slate-50 lg:grid lg:grid-cols-[1.05fr_0.95fr]" data-testid="pathlync-auth-page">
      <section className="flex flex-col justify-center bg-[#0f172a] px-6 py-14 text-slate-100 lg:px-14">
        <div className="flex items-center gap-2">
          <Microscope size={22} className="text-sky-300" />
          <span className="font-display text-xl font-bold tracking-tight">PathLync</span>
        </div>
        <h1 className="mt-8 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
          Remote pathology consultation for laboratories that were priced out of it.
        </h1>
        <p className="mt-5 max-w-md text-base leading-relaxed text-slate-300">
          Small and community laboratories deserve a second opinion without an enterprise
          telepathology contract. PathLync needs nothing more than the camera you already own.
        </p>
        <ul className="mt-8 space-y-3">
          {POINTS.map((p) => (
            <li key={p} className="flex gap-3 text-sm text-slate-200">
              <Check size={17} className="mt-0.5 shrink-0 text-sky-300" /> {p}
            </li>
          ))}
        </ul>
      </section>

      <section className="flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-md">
          <div className="mb-6 flex border border-slate-300">
            {[["signin", "Sign in"], ["register", "Create an account"]].map(([key, text]) => (
              <button key={key} onClick={() => { setMode(key); setError(""); }}
                      data-testid={`pathlync-tab-${key}`}
                      className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                        mode === key ? "bg-[#1e3a8a] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {text}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4 border border-slate-200 bg-white p-6">
            {mode === "register" && (
              <>
                <div>
                  <Label className="mb-2 block">I am signing up as</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[["lab_user", "Laboratory", Building2], ["pathologist", "Pathologist", Stethoscope]]
                      .map(([key, text, Icon]) => (
                        <button type="button" key={key} onClick={() => setRole(key)}
                                data-testid={`pathlync-role-${key}`}
                                className={`flex flex-col items-center gap-1 border p-3 text-xs font-semibold transition-colors ${
                                  role === key ? "border-[#1e3a8a] bg-[#1e3a8a]/5 text-[#1e3a8a]"
                                    : "border-slate-300 text-slate-600 hover:border-slate-400"}`}>
                          <Icon size={18} /> {text}
                        </button>
                      ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="pt-name">Full name</Label>
                  <Input id="pt-name" required value={form.name} onChange={set("name")}
                         data-testid="pathlync-name-input" placeholder="Dana Reyes" />
                </div>
                <div>
                  <Label htmlFor="pt-inst">
                    {role === "pathologist" ? "Practice or group" : "Laboratory"}
                  </Label>
                  <Input id="pt-inst" value={form.institution} onChange={set("institution")}
                         data-testid="pathlync-institution-input" placeholder="Community Pathology Associates" />
                </div>
                {role === "pathologist" && (
                  <div>
                    <Label htmlFor="pt-cred">Credentials (printed on your reports)</Label>
                    <Input id="pt-cred" value={form.credentials} onChange={set("credentials")}
                           data-testid="pathlync-credentials-input" placeholder="MD, FCAP" />
                  </div>
                )}
              </>
            )}
            <div>
              <Label htmlFor="pt-email">Email</Label>
              <Input id="pt-email" type="email" required value={form.email} onChange={set("email")}
                     data-testid="pathlync-email-input" placeholder="you@laboratory.org" />
            </div>
            <div>
              <Label htmlFor="pt-password">Password</Label>
              <Input id="pt-password" type="password" required value={form.password}
                     onChange={set("password")} data-testid="pathlync-password-input"
                     placeholder={mode === "register" ? "At least 8 characters" : ""} />
            </div>
            {error && (
              <p className="text-sm font-semibold text-red-700" data-testid="pathlync-auth-error">{error}</p>
            )}
            <Button type="submit" disabled={busy} data-testid="pathlync-submit-button"
                    className="w-full bg-[#1e3a8a] hover:bg-[#172f6e]">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              {mode === "signin" ? "Sign in" : "Create account"}
              {!busy && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </form>
          <p className="mt-4 text-center text-xs text-slate-500">
            PathLync is for laboratory professional use. Consultation opinions are rendered on the
            images supplied and are not a primary diagnosis.
          </p>
        </div>
      </section>
    </div>
  );
}
